import React, { useState, useEffect, useCallback, useMemo } from "react";
import { usePagination } from "../hooks";
import {
  Box,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TableFooter,
  Grid,
  InputBase,
  Checkbox,
  FormControlLabel,
  Button,
  CircularProgress,
  Skeleton,
  Chip,
  Collapse,
} from "@mui/material";
import {
  Search as SearchIcon,
  Assignment as AssignmentIcon,
  Build as BuildIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  ChevronRight as ChevronRightIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import {
  getFullPoAndMo,
  getFullReparaciones,
} from "../services/ConnectDataBase";
import { formatDateTime } from "../services/Functions.js";
import { SCREEN_IDS } from "../components/FullComponent.js";
import { typeConnect, backgroundColor2 } from "../constants/Constants";
import { translateText } from "../translations/translationService";
import { OVERALL_STATUS_SCREEN_COLORS } from "../style/colors.js";

const OverallStatus = () => {
  const lang = useSelector((state) => state.lang.value);
  const [isLoading, setIsLoading] = useState(true);
  const screenName = "OverallStatusScreen";
  const currentScreenId = SCREEN_IDS.OVERALLSTATUS;
  const [listPO, setListPO] = useState([]);
  const [expandedPO, setExpandedPO] = useState(null);
  const [refSearch, setRefSearch] = useState("");
  const [searchResult, setSearchResult] = useState(null);
  const [expandedLines, setExpandedLines] = useState({});
  const [datosLineaMO, setDatosLineaMo] = useState({});
  const [listDataForMo, setListDataForMo] = useState({}); // Cambiado a objeto para almacenar por PO y MO
  const [showIncompleteOnly, setShowIncompleteOnly] = useState(false);
  const [filteredMOCount, setFilteredMOCount] = useState(0);
  const [itemsToRender, setItemsToRender] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [currentView, setCurrentView] = useState("MOs");

  // Hook de paginación
  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } = usePagination(0, 25);

  const getPo = useCallback(async () => {
    setIsLoading(true);
    try {
      let data;
      if (currentView === "MOs") {
        data = await getFullPoAndMo(
          screenName,
          typeConnect[0],
          currentScreenId
        );

      } else if (currentView === "Reparaciones") {
        data = await getFullReparaciones(
          screenName,
          typeConnect[0],
          currentScreenId
        );
      }

      // Optimizar procesamiento de datos
      const poArray = Object.entries(data || {}).map(([po, moArray]) => {
        const processedMOs = (moArray || []).map((moItem) => {
          if (!moItem) return null;
          return {
            ...moItem,
            fecha_fin: moItem.fecha_fin ? formatDateTime(moItem.fecha_fin) : null,
            fecha_ini: moItem.fecha_ini ? formatDateTime(moItem.fecha_ini) : null,
            fecha_entrega_pieza: moItem.fecha_entrega_pieza
              ? formatDateTime(moItem.fecha_entrega_pieza)
              : null,
            fecha_recibida_pieza: moItem.fecha_recibida_pieza
              ? formatDateTime(moItem.fecha_recibida_pieza)
              : null,
            peso: moItem.peso
              ? typeof moItem.peso === "object"
                ? moItem.peso.date || null
                : moItem.peso
              : null,
            duracion: moItem.duracion
              ? typeof moItem.duracion === "object"
                ? moItem.duracion.date || null
                : moItem.duracion
              : null,
          };
        }).filter(Boolean);
        
        return {
          key: po,
          po,
          mo: processedMOs,
        };
      });
      setIsLoading(false);
      setListPO(poArray);
    } catch (error) {
      setIsLoading(false);
      console.error(error);
    }
  }, [currentView, screenName, currentScreenId]);

  useEffect(() => {
    getPo();
  }, [getPo]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      handleSearch(searchText);
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchText]);

  // Optimizar filtrado con useMemo
  const { filteredItems, filteredCount } = useMemo(() => {
    let items = [];

    if (searchResult) {
      items = Array.isArray(searchResult) ? searchResult : [searchResult];
    } else {
      items = listPO;
    }

    if (showIncompleteOnly) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 5);

      const filteredItems = items
        .map((item) => {
          const moArray = Array.isArray(item.mo) ? item.mo : [item.mo];
          const filteredMo = moArray.filter((moItem) => {
            if (!moItem) return false;

            const moFechaIni = new Date(formatDateTime(moItem.fecha_fin));
            const isCalidadAndPast =
              moItem.tipo &&
              moItem.tipo.includes("CALIDAD") &&
              moFechaIni < yesterday;

            const isCalidadAndRepair = !!(
              moItem.tipo?.includes("CALIDAD") && moItem.mo?.startsWith("8")
            );

            const terminada =
              parseFloat(moItem.cantidad) >
                (parseFloat(moItem.cantidad_fabricada) ?? 0) ||
              parseFloat(moItem.cantidad) >
                (parseFloat(moItem.cantidad_terminada) ?? 0);

            return (
              moItem.tipo !== "Term." &&
              moItem.anulada !== "S" &&
              moItem.tipo &&
              moItem.tipo !== "" &&
              !isCalidadAndPast &&
              terminada &&
              !isCalidadAndRepair
            );
          });

          return filteredMo.length > 0 ? { ...item, mo: filteredMo } : null;
        })
        .filter(Boolean);

      return {
        filteredItems,
        filteredCount: filteredItems.length,
      };
    }

    return {
      filteredItems: items,
      filteredCount: 0,
    };
  }, [searchResult, listPO, showIncompleteOnly]);

  useEffect(() => {
    setFilteredMOCount(filteredCount);
    setItemsToRender(filteredItems);
  }, [filteredItems, filteredCount]);

  const toggleShowIncompleteOnly = () => {
    setShowIncompleteOnly((prevShowIncompleteOnly) => !prevShowIncompleteOnly);
  };

  const handleExpand = useCallback((po) => {
    setExpandedPO((prevExpanded) => (prevExpanded === po ? null : po));
  }, []);

  const handleExpandLine = useCallback((mo, po) => {
    const result = listPO.find((item) => item.po === mo.po);

    if (!result) return;

    const filteredMO = result.mo.filter((item) => item.mo === mo.mo);
    const isExpanding = !expandedLines[po]?.[mo.mo];
    
    setExpandedLines((prevExpanded) => ({
      ...prevExpanded,
      [po]: {
        ...prevExpanded[po],
        [mo.mo]: !prevExpanded[po]?.[mo.mo],
      },
    }));

    // Almacenar datos por PO y MO para que cada MO expandida tenga sus propios datos
    if (isExpanding) {
      setListDataForMo((prev) => ({
        ...prev,
        [po]: {
          ...prev[po],
          [mo.mo]: filteredMO,
        },
      }));
    } else {
      // Si se está colapsando, eliminar los datos
      setListDataForMo((prev) => {
        const newData = { ...prev };
        if (newData[po]) {
          const { [mo.mo]: removed, ...rest } = newData[po];
          newData[po] = rest;
          if (Object.keys(newData[po]).length === 0) {
            delete newData[po];
          }
        }
        return newData;
      });
    }
    
    setDatosLineaMo((prev) => ({
      ...prev,
      [po]: {
        ...prev[po],
        [mo.mo]: mo,
      },
    }));
  }, [listPO, expandedLines]);

  // Memoizar el procesamiento de MOs por PO
  const processedMOsByPO = useMemo(() => {
    const processed = {};
    itemsToRender.forEach((item) => {
      const moGroups = (item.mo || [])
        .filter((mo) => mo && mo.mo)
        .reduce((groups, mo) => {
          const moNumber = mo.mo.split("-")[1];
          groups[moNumber] = mo;
          return groups;
        }, {});

      processed[item.po] = Object.values(moGroups).sort((a, b) =>
        a.mo.localeCompare(b.mo)
      );
    });
    return processed;
  }, [itemsToRender]);

  const renderItem = useCallback((item) => {
    const isExpanded = expandedPO === item.po;
    const nearestMOs = processedMOsByPO[item.po] || [];

    return (
      <React.Fragment key={item.po}>
        <TableRow 
          hover 
          onClick={() => handleExpand(item.po)}
          sx={{
            cursor: "pointer",
            "&:hover": {
              backgroundColor: "rgba(70, 93, 100, 0.05)",
            },
          }}>
          <TableCell
            colSpan={8}
            sx={{
              backgroundColor: backgroundColor2,
              color: "white",
              textAlign: "center",
              py: 1,
              px: 2,
            }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              {isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: 20 }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 20 }} />
              )}
              <Typography variant='subtitle1' sx={{ fontWeight: 600, fontSize: "0.95rem" }}>
                {item.po}
              </Typography>
              <Chip
                label={`${nearestMOs.length} MO${nearestMOs.length !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  color: "white",
                }}
              />
            </Box>
          </TableCell>
        </TableRow>
        {isExpanded && (
          <>
            {nearestMOs.map((nearestMO) => {
            let backgroundColor = OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_NEUTRAL;
            if (nearestMO.anulada === "S") {
              backgroundColor = OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_ERROR;
            } else if (
              parseFloat(nearestMO.cantidad) <=
              parseFloat(nearestMO.cantidad_fabricada ?? 0)
            ) {
              backgroundColor = OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_SUCCESS;
            } else if (
              parseFloat(nearestMO.cantidad) <=
              parseFloat(nearestMO.cantidad_terminada ?? 0)
            ) {
              backgroundColor = OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_SUCCESS;
            }
            nearestMO.po = item.po;

            const isLineExpanded = expandedLines[item.po]?.[nearestMO.mo];

            return (
              <React.Fragment key={nearestMO.mo}>
                <TableRow
                  hover
                  sx={{
                    backgroundColor,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      opacity: 0.9,
                      transform: "scale(1.01)",
                    },
                  }}
                  onClick={() => handleExpandLine(nearestMO, item.po)}>
                  <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {isLineExpanded ? (
                        <ChevronRightIcon sx={{ fontSize: 16, transform: "rotate(90deg)" }} />
                      ) : (
                        <ChevronRightIcon sx={{ fontSize: 16 }} />
                      )}
                      {nearestMO.mo}
                    </Box>
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5 }}>{nearestMO.referencia}</TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5 }}>{nearestMO.team}</TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5 }}>{nearestMO.usuario}</TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5 }}>
                    <Chip
                      label={nearestMO.tipo || "-"}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: "0.7rem",
                        backgroundColor: "rgba(0, 0, 0, 0.05)",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5 }}>
                    {nearestMO.fecha_fin &&
                    nearestMO.fecha_fin !== "0000-00-00 00:00:00"
                      ? formatDateTime(nearestMO.fecha_fin)
                      : formatDateTime(nearestMO.fecha_ini)}
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5, maxWidth: 200 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "0.75rem",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}>
                      {nearestMO.anulada === "S"
                        ? "Anulada"
                        : nearestMO.tipo
                        ? nearestMO.incidencia
                        : "No tiene procesos Activos"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5 }}>
                    {nearestMO.tipo_tiempo === "WEIGHT" &&
                    nearestMO.peso !== "0.0000" &&
                    nearestMO.peso !== null
                      ? nearestMO.tipo === "Taller externo"
                        ? nearestMO.peso.toString().startsWith(".")
                          ? `0${nearestMO.peso}`
                          : nearestMO.peso
                        : nearestMO.peso.toString().startsWith(".")
                        ? `0${nearestMO.peso} gr.`
                        : `${nearestMO.peso} gr.`
                      : nearestMO.tipo_tiempo === "TIME_WORK" &&
                        nearestMO.duracion &&
                        nearestMO.duracion !== null
                      ? formatDateTime(nearestMO.duracion).split(" ")[1]
                      : " - "}
                  </TableCell>
                </TableRow>
                {isLineExpanded && 
                 Array.isArray(listDataForMo[item.po]?.[nearestMO.mo]) && 
                 listDataForMo[item.po][nearestMO.mo].length > 0 && (
                  <>
                    {listDataForMo[item.po][nearestMO.mo].map((data, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_HOVER,
                          position: "relative",
                          width: "100%",
                          "&:hover": {
                            backgroundColor: "rgba(70, 93, 100, 0.08)",
                          },
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            left: 0,
                            top: 0,
                            bottom: 0,
                            width: "3px",
                            backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BORDER_BRAND,
                          },
                        }}>
                        {/* Columna MO comentada - ya se muestra en la fila verde clara de arriba */}
                        {/* <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5, width: "auto" }}> */}
                          {/* <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Box
                              sx={{
                                width: 4,
                                height: 4,
                                borderRadius: "50%",
                                backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BORDER_BRAND,
                                flexShrink: 0,
                              }}
                            /> */}
                            {/* {data.mo} - Comentado para mejor alineación */}
                          {/* </Box> */}
                        {/* </TableCell> */}
                        <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5, width: "auto" }}>
                          {data.referencia}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5, width: "auto" }}>
                          {data.team}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5, width: "auto" }}>
                          {data.usuario}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5, width: "auto" }}>
                          {data.tipo === "ASIGNAR" ? (
                            <Chip
                              label={`${data.tipo} (${data.recibe_pieza})`}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                backgroundColor: "rgba(70, 93, 100, 0.1)",
                              }}
                            />
                          ) : (
                            <Chip
                              label={data.tipo || "-"}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: "0.7rem",
                                backgroundColor: "rgba(0, 0, 0, 0.05)",
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5, width: "auto" }}>
                          {data.fecha_fin &&
                          data.fecha_fin !== "0000-00-00 00:00:00"
                            ? formatDateTime(data.fecha_fin)
                            : formatDateTime(data.fecha_ini)}
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5, maxWidth: 200, width: "auto" }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontSize: "0.75rem",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                            }}>
                            {data.tipo
                              ? data.incidencia
                              : "No tiene procesos Activos"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 0.75, fontSize: "0.8rem", px: 1.5, width: "auto" }}>
                          {data.tipo_tiempo === "WEIGHT" &&
                          data.peso !== "0.0000" &&
                          data.peso !== null
                            ? data.tipo === "Taller externo"
                              ? data.peso.toString().startsWith(".")
                                ? `0${data.peso}`
                                : data.peso
                              : data.peso.toString().startsWith(".")
                              ? `0${data.peso} gr.`
                              : `${data.peso} gr.`
                            : data.tipo_tiempo === "TIME_WORK" &&
                              data.duracion &&
                              data.duracion !== null
                            ? formatDateTime(data.duracion).split(" ")[1]
                            : " - "}
                        </TableCell>
                      </TableRow>
                    ))}
                  </>
                )}
              </React.Fragment>
            );
          })}
          </>
        )}
      </React.Fragment>
    );
  }, [expandedPO, processedMOsByPO, expandedLines, listDataForMo, handleExpand, handleExpandLine]);

  const handleSearch = (searchText) => {
    if (typeof searchText !== "string") {
      searchText = "";
    }
    const lowercasedText = searchText.toLowerCase();

    if (lowercasedText === "") {
      setSearchResult(null);
      return;
    }

    const result = listPO.filter((item) =>
      item.mo?.some(
        (moItem) =>
          moItem &&
          ["po", "mo", "referencia", "cliente", "usuario", "team", "tipo"]
            .map((key) => {
              const value = moItem[key];
              if (value != null) {
                return value.toString().toLowerCase().includes(lowercasedText);
              }
              return false;
            })
            .some((match) => match)
      )
    );

    setSearchResult(result);
  };

  const handleSearchInputChange = (e) => {
    const text = e.target.value;
    setRefSearch(text);
    setSearchText(text);
  };

  // Skeleton loader para mejor UX
  const SkeletonRow = () => (
    <TableRow>
      {[...Array(8)].map((_, i) => (
        <TableCell key={i} sx={{ py: 1 }}>
          <Skeleton variant="text" width="100%" height={20} />
        </TableCell>
      ))}
    </TableRow>
  );

  return (
    <Box sx={{ padding: 1.5 }}>
      <Grid container spacing={1.5}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 1.5,
              mb: 1.5,
              backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_CARD,
              borderRadius: 2,
            }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
              {/* Búsqueda */}
              <Box sx={{ flex: 1, minWidth: "250px", position: "relative" }}>
                <InputBase
                  value={refSearch}
                  onChange={handleSearchInputChange}
                  placeholder='PO / MO / REF'
                  sx={{
                    width: "100%",
                    backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_INPUT,
                    color: OVERALL_STATUS_SCREEN_COLORS.TEXT_CARD,
                    fontSize: "0.85rem",
                    fontWeight: 500,
                    padding: "8px 12px 8px 40px",
                    borderRadius: 2,
                    border: `1.5px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_CARD}`,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      borderColor: OVERALL_STATUS_SCREEN_COLORS.BORDER_BRAND,
                      boxShadow: "0 2px 4px rgba(70, 93, 100, 0.1)",
                    },
                    "&.Mui-focused": {
                      backgroundColor: "white",
                      borderColor: OVERALL_STATUS_SCREEN_COLORS.BORDER_BRAND,
                      boxShadow: "0 0 0 2px rgba(70, 93, 100, 0.1)",
                    },
                    "& input": {
                      padding: 0,
                    },
                    "& input::placeholder": {
                      color: OVERALL_STATUS_SCREEN_COLORS.TEXT_MUTED,
                      opacity: 1,
                    },
                  }}
                />
                <SearchIcon
                  sx={{
                    position: "absolute",
                    left: 12,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: OVERALL_STATUS_SCREEN_COLORS.TEXT_MUTED,
                    fontSize: 18,
                    pointerEvents: "none",
                  }}
                />
              </Box>

              {/* Botones de vista */}
              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  variant={currentView === "MOs" ? "contained" : "outlined"}
                  size="small"
                  startIcon={<AssignmentIcon />}
                  onClick={() => setCurrentView("MOs")}
                  sx={{
                    backgroundColor:
                      currentView === "MOs"
                        ? OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY
                        : "transparent",
                    color:
                      currentView === "MOs"
                        ? OVERALL_STATUS_SCREEN_COLORS.TEXT_WHITE
                        : OVERALL_STATUS_SCREEN_COLORS.TEXT_BRAND,
                    border: `1.5px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_BRAND}`,
                    borderRadius: 2,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    padding: "6px 16px",
                    textTransform: "none",
                    minWidth: 100,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        currentView === "MOs"
                          ? OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY
                          : "rgba(70, 93, 100, 0.1)",
                      transform: "translateY(-1px)",
                    },
                  }}>
                  {translateText("MOs", lang)}
                </Button>
                <Button
                  variant={currentView === "Reparaciones" ? "contained" : "outlined"}
                  size="small"
                  startIcon={<BuildIcon />}
                  onClick={() => setCurrentView("Reparaciones")}
                  sx={{
                    backgroundColor:
                      currentView === "Reparaciones"
                        ? OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY
                        : "transparent",
                    color:
                      currentView === "Reparaciones"
                        ? OVERALL_STATUS_SCREEN_COLORS.TEXT_WHITE
                        : OVERALL_STATUS_SCREEN_COLORS.TEXT_BRAND,
                    border: `1.5px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_BRAND}`,
                    borderRadius: 2,
                    fontSize: "0.8rem",
                    fontWeight: 600,
                    padding: "6px 16px",
                    textTransform: "none",
                    minWidth: 120,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor:
                        currentView === "Reparaciones"
                          ? OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_BUTTON_SECONDARY
                          : "rgba(70, 93, 100, 0.1)",
                      transform: "translateY(-1px)",
                    },
                  }}>
                  {translateText("Reparaciones", lang)}
                </Button>
              </Box>

              {/* Título y contador */}
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: OVERALL_STATUS_SCREEN_COLORS.TEXT_BRAND,
                  minWidth: 120,
                }}>
                {translateText("Tabla de PO", lang)}
                {filteredMOCount > 0 && (
                  <Chip
                    label={filteredMOCount}
                    size="small"
                    sx={{
                      ml: 1,
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_BUTTON_PRIMARY,
                      color: OVERALL_STATUS_SCREEN_COLORS.TEXT_WHITE,
                    }}
                  />
                )}
              </Typography>

              {/* Checkbox filtro */}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={showIncompleteOnly}
                    onChange={toggleShowIncompleteOnly}
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {showIncompleteOnly
                      ? translateText("MO Activas", lang)
                      : translateText("Total MO", lang)}
                  </Typography>
                }
                sx={{ ml: 0 }}
              />
            </Box>
          </Paper>
        </Grid>

        {/* Tabla de PO y MO */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              overflow: "hidden",
              backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_WHITE,
              borderRadius: 2,
            }}>
            {isLoading ? (
              <Box sx={{ p: 2 }}>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {[...Array(8)].map((_, i) => (
                          <TableCell key={i} sx={{ py: 1 }}>
                            <Skeleton variant="text" width="100%" height={24} />
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {[...Array(5)].map((_, i) => (
                        <SkeletonRow key={i} />
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            ) : (
              <TableContainer sx={{ maxHeight: "78vh" }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_TABLE}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                        }}>
                        {translateText("MO", lang)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_TABLE}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                        }}>
                        {translateText("Referencia", lang)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_TABLE}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                        }}>
                        {translateText("Team", lang)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_TABLE}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                        }}>
                        {translateText("Usuario", lang)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_TABLE}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                        }}>
                        {translateText("Tipo", lang)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_TABLE}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                        }}>
                        {translateText("Fec. Fin", lang)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_TABLE}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                        }}>
                        {translateText("Incidencia", lang)}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_TABLE_HEADER,
                          borderBottom: `1px solid ${OVERALL_STATUS_SCREEN_COLORS.BORDER_TABLE}`,
                          fontSize: "0.75rem",
                          color: "black",
                          py: 1,
                          px: 1.5,
                        }}>
                        {translateText("Tiempo / Peso", lang)}
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {itemsToRender.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                          <Typography variant="body2" color="text.secondary">
                            {translateText("No hay datos disponibles", lang)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      itemsToRender
                        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                        .map((item) => renderItem(item))
                    )}
                  </TableBody>
                  <TableFooter>
                    <TableRow
                      sx={{
                        position: "sticky",
                        bottom: 0,
                        backgroundColor: OVERALL_STATUS_SCREEN_COLORS.BACKGROUND_WHITE,
                        zIndex: 1,
                      }}>
                      <TablePagination
                        colSpan={8}
                        rowsPerPageOptions={[25, 50, 100]}
                        count={itemsToRender.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage={translateText("Filas por página", lang)}
                        labelDisplayedRows={({ from, to, count }) =>
                          `${from}-${to} ${translateText("de", lang)} ${count}`
                        }
                        sx={{
                          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                            fontSize: "0.75rem",
                          },
                        }}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OverallStatus;
